class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;         // <- NO fijo: se regenera en cada búsqueda
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input?.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input?.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery(){ return (this.input?.value || '').trim(); }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) { this.close(true); return; }

    // controller fresco SIEMPRE
    try { this.abortController?.abort(); } catch(_){}
    this.abortController = new AbortController();
    this.getSearchResults(this.searchTerm, this.abortController.signal);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      try { this.abortController?.abort(); } catch(_){}
      this.abortController = null;
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      try { this.abortController?.abort(); } catch(_){}
      this.abortController = new AbortController();
      this.getSearchResults(this.searchTerm, this.abortController.signal);
    }
  }

  onFocusOut(){ setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }); }

  onKeyup(e) {
    if (!this.getQuery().length) this.close(true);
    e.preventDefault();
    if (e.code === 'ArrowUp') this.switchOption('up');
    else if (e.code === 'ArrowDown') this.switchOption('down');
    else if (e.code === 'Enter') this.selectOption();
  }

  onKeydown(e){ if (e.code === 'ArrowUp' || e.code === 'ArrowDown') e.preventDefault(); }

  updateSearchForTerm(prev, next) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const current = el?.innerText;
    if (!current || !prev) return;
    const re = new RegExp(prev.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    const matches = current.match(re);
    if (matches && matches.length > 1) return;
    el.innerText = current.replace(re, next);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const list = Array.from(this.querySelectorAll('li, button.predictive-search__item'))
      .filter(el => el.offsetParent !== null);
    if (moveUp && !selected) return;

    let idx = list.indexOf(selected);
    let next = 0;
    if (!moveUp && selected) next = idx === list.length - 1 ? 0 : idx + 1;
    else if (moveUp) next = idx === 0 ? list.length - 1 : idx - 1;

    if (next === idx) return;
    const active = list[next];
    active?.setAttribute('aria-selected', true);
    selected?.setAttribute('aria-selected', false);
    this.input?.setAttribute('aria-activedescendant', active?.id || '');
  }

  selectOption(){ this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')?.click(); }

  getSearchResults(searchTerm, signal) {
    const key = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) { this.renderSearchResults(this.cachedResults[key]); return; }

    const path = (window.routes && window.routes.predictive_search_url) ? window.routes.predictive_search_url : '/search/suggest';
    const url = `${path}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;

    fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'text/html' }, credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) { this.close(); throw new Error(res.statusText || String(res.status)); }
        return res.text();
      })
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
        const markup = section ? section.innerHTML : '';
        this.allPredictiveSearchInstances.forEach((i) => { i.cachedResults[key] = markup; });
        this.renderSearchResults(markup);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError' && err?.code !== 20) console.warn('[PredictiveSearch] fetch error:', err);
      })
      .finally(() => { this.removeAttribute('loading'); }); // <- nunca queda colgado
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup) {
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = markup || '';
    if (markup) {
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
    } else {
      this.removeAttribute('results');
      this.close();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input?.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clear = false) {
    this.closeResults(clear);
    this.isOpen = false;
  }

  closeResults(clear = false) {
    if (clear) { if (this.input) this.input.value = ''; this.removeAttribute('results'); }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', false);
    this.input?.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading'); this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}
customElements.define('predictive-search', PredictiveSearch);
